/* global SideDrawer, trapFocus, removeTrapFocus */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * Also called directly by siblingLinkHandler.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Handle an anchor tag
      if (evt.target.tagName === 'A') {
        evt.preventDefault();
      }

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // Update product links
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');
      if (this.notification) this.notification.hidden = true;

      this.header = this.querySelector('.js-quick-add-heading');

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      // if (this.productUrl && this.productUrl === this.productUrl) {
      //   if (opener.dataset.selectedColor) this.setActiveVariant(opener);
      //   super.open(opener);
      //   opener.removeAttribute('aria-disabled');
      //   return;
      // }

      this.productUrl = opener.tagName === 'A' ? opener.href : opener.dataset.productUrl;
      this.header.innerHTML = '';
      this.content.innerHTML = '';
      document.querySelector('.quick-add__header--price')?.remove();
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      const response = await fetch(this.productUrl);
      if (response.ok) {
        const pageContentHtml = await response.text();
        this.renderProduct(opener, pageContentHtml);

        if (this.querySelector('.shopify-payment-button')) {
          setTimeout(() => {
            removeTrapFocus();
            trapFocus(this);
          }, 1000);
        } else {
          removeTrapFocus();
          trapFocus(this);
        }
      }

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      document.querySelector('.js-overlay').innerHTML = '';
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     * @param {string} pageContentHtml - Product page HTML for the product we wish to show.
     */
    renderProduct(opener, pageContentHtml) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = pageContentHtml;
      this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');

      // Load any external resources
      tmpl.content.querySelectorAll('.cc-main-product link[rel="stylesheet"]').forEach((el) => {
        if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
          document.head.insertAdjacentHTML('beforeend', el.outerHTML);
        }
      });
      tmpl.content.querySelectorAll('.cc-main-product script[src]').forEach((el) => {
        if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
          const scr = document.createElement('script');
          scr.src = el.getAttribute('src');
          scr.defer = 'defer';
          document.head.appendChild(scr);
        }
      });

      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Handle size chart modal: move to body so position:fixed works outside the drawer's stacking context.
      const sizeChartModal = this.productEl.querySelector('#size-chart');
      document.getElementById('size-chart')?.remove();
      if (sizeChartModal) {
        document.body.appendChild(sizeChartModal);
      } else {
        this.productEl.querySelector('[data-modal="size-chart"]')?.remove();
      }

      this.updateContent();
      this.updateForm();
      this.initGloboOptions();

      if (opener.dataset.selectedColor) {
        this.setActiveVariant(opener);
      }

      if (this.querySelector('.product-options__siblings a')) {
        this.querySelectorAll('.product-options__siblings a').forEach((el) => {
          el.addEventListener('click', this.siblingLinkHandler.bind(this));
        });
      }
    }

    /**
     * Handling selecting a sibling in the product form
     * @param {object} evt - Event object.
     */
    siblingLinkHandler(evt) {
      evt.preventDefault();
      const qbBtn = document.createElement('button');
      qbBtn.className = 'js-quick-add';
      qbBtn.dataset.productUrl = evt.currentTarget.getAttribute('href');
      this.handleDocumentClick({ target: qbBtn });
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        setTimeout(() => {
          this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
            .click();
        }, 100);
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      this.header.innerHTML = theme.strings.quickAddHeading;

      const productMediaHtml = this.getElementHtml('#product-media');

      // Remove the thumbnails if present
      const parser = new DOMParser();
      const productMedia = parser.parseFromString(productMediaHtml, 'text/html');
      productMedia.querySelector('[data-layout="stacked"]')?.setAttribute('data-layout', 'slider');
      const mediaCount = productMedia.querySelectorAll('.media-viewer__item')?.length || 0;
      const thumbs = productMedia.querySelector('.media-gallery__thumbs');
      if (thumbs) thumbs.remove();

      this.content.innerHTML = `
        <div class="product-media product-media--slider mt-8 mb-8${mediaCount === 1 ? ' product-media--single-slider' : ''}">
          ${productMedia.body.innerHTML}
        </div>
        <div class="quick-add-info grid mb-2">
          <div class="quick-add-info__details">
            <div class="product-labels mb-3">
              ${this.getElementHtml('.product-labels')}
            </div>
            <div class="product-vendor-sku mb-1 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h2 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            <div class="product-subtitle mb-1 text-sm">
              ${this.getElementHtml('.product-subtitle')}
            </div>
            <div class="product-rating flex items-baseline mb-3">
              ${this.getElementHtml('.product-rating')}
            </div>
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <div class="product-backorder">
              ${this.getElementHtml('.product-backorder')}
            </div>
          </div>
        </div>
        <div class="product-options mt-8">
          ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
        </div>
        <div class="text-theme-text mt-6">
          <a href="${this.productUrl}" class="link text-sm font-bold js-prod-link">
            ${theme.strings.viewDetails}
          </a>
        </div>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');

      this.initCountdowns();
    }

    initCountdowns() {
      this.querySelectorAll('.cpf-cd[data-end]').forEach(function(el) {
        if (el.dataset.cdInit) return;
        el.dataset.cdInit = 'true';
        var end = new Date(el.dataset.end).getTime();
        if (isNaN(end)) return;
        function pad(n) { return String(n).padStart(2, '0'); }
        function tick() {
          var diff = end - Date.now();
          if (diff <= 0) { el.style.display = 'none'; return; }
          var d = Math.floor(diff / 86400000);
          var h = Math.floor((diff % 86400000) / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          var s = Math.floor((diff % 60000) / 1000);
          el.querySelector('[data-cd-days]').textContent    = pad(d);
          el.querySelector('[data-cd-hours]').textContent   = pad(h);
          el.querySelector('[data-cd-minutes]').textContent = pad(m);
          el.querySelector('[data-cd-seconds]').textContent = pad(s);
        }
        tick();
        setInterval(tick, 1000);
      });
    }

    async initGloboOptions() {
      if (typeof GPOConfigs === 'undefined' || typeof GPOConfigs.loadMainScripts !== 'function') return;
      const drawerForm = this.querySelector('form.js-product-form');
      if (!drawerForm) return;
      try {
        const handle = this.productUrl.split('/products/')[1]?.split('?')[0];
        if (!handle) return;
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return;
        const product = await res.json();
        await GPOConfigs.loadMainScripts(product, null, drawerForm);
        const gpoApp = this.querySelector('.gpo-app');
        if (gpoApp) {
          const anchor = this.content.querySelector('.product-options') || this.content.querySelector('.text-theme-text');
          if (anchor) anchor.insertAdjacentElement('beforebegin', gpoApp);
          else this.content.appendChild(gpoApp);
        }
      } catch (e) {}
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();
      } else {
        this.form.innerHTML = '';
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
